'use client'

// React Imports
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'

// MUI Imports
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import LaunchIcon from '@mui/icons-material/Launch'
import Box from '@mui/material/Box'

// Third-party Imports
import { Icon } from '@iconify/react'
import { createColumnHelper } from '@tanstack/react-table'

// Component Imports
import PremiumModuleWrapper from '../../../../Task/Components/common/PremiumModuleWrapper'
import OptionMenu from '../../../../../components/common/Template_Layouts/@core/components/option-menu'
import EntityEdit from '../../../../../components/common/EntityEdit/EntityEdit'
import ScoreCard from '../../../../../components/common/ScoreCard/ScoreCard'
import Loader from '../../../../../components/Loader/Loader'
import AppointmentTypeIcon from '../../AppointmentTypeIcon'

// Util Imports
import { getInitials } from '../../../../../utils/helpers'
import { TASK_ACTIONS, getStatusDescriptor, formatDate } from '../../../../Task/Components/common/premiumUtils'
import API_ENDPOINTS from '../../../../../config/apiEndpoints'

const API_BASE_WCF = (process.env.REACT_APP_WCF_API_BASE_URL || '').replace(/\/$/, '');
const columnHelper = createColumnHelper()

const PhysicalAppointmentListWrapper = ({
    data = [],
    searchTerm,
    stats = { total: 0, completed: 0, overdue: 0, open: 0 },
    loading = false,
    onEditAppointment,
    onRemoveAppointment,
    onViewAppointment,
    onMarkCompleted,
    onViewLead,
    onRefresh,
    onAddComment,
    currentPage,
    onPageChange,
    itemsPerPage,
    crmOperationPerms
}) => {
    // States
    const [totalRecords, setTotalRecords] = useState(0)
    const [statusFilter, setStatusFilter] = useState('All')
    const [showEntityEdit, setShowEntityEdit] = useState(false)
    const [selectedEntity, setSelectedEntity] = useState(null)
 
    const callbacksRef = useRef({})
    callbacksRef.current = {
        onViewAppointment,
        onEditAppointment,
        onAddComment,
        onMarkCompleted,
        onRemoveAppointment,
        onViewLead
    }

    const getAvatar = params => {
        const { avatar, name, threshold } = params
        const colorSchemes = [
            { bg: '#fdf2f8', text: '#db2777' }, // Pink
            { bg: '#f0fdf4', text: '#16a34a' }, // Green
            { bg: '#eff6ff', text: '#2563eb' }, // Blue
            { bg: '#f5f3ff', text: '#7c3aed' }, // Purple
            { bg: '#fff7ed', text: '#ea580c' }, // Orange
            { bg: '#fffbeb', text: '#d97706' }, // Amber
        ]
        const scheme = colorSchemes[(name || '').length % colorSchemes.length] || colorSchemes[2]

        return (
            <div className="lead-avatar-big">
                <div className="avatar-circle" style={{ backgroundColor: scheme.bg, color: scheme.text }}>
                    {avatar ? (
                        <img src={avatar} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        getInitials(name || 'Unknown')
                    )}
                </div>
                <div className="score-badge-wrapper">
                    <span className="score-badge">{threshold ?? 0}</span>
                </div>
            </div>
        )
    }

    const getAppointmentTypeIcon = row => {
        return row?.IconClass || row?.iconClass || 'appointment'
    }

    const columns = useMemo(() => [
        columnHelper.accessor('taskTitle', {
            header: 'APPOINTMENT',
            cell: ({ row }) => {
                const statusLabel = getStatusDescriptor(
                    row.original.taskStatus,
                    row.original.endDate,
                    row.original.completedDate,
                    row.original.taskTitle,
                    row.original.startDate,
                    row.original.statusLabel,
                    row.original.outcomeName,
                    row.original.createdDate
                )

                let chipColor = 'info'
                const lowerLabel = statusLabel.toLowerCase()
                if (lowerLabel.includes('overdue')) chipColor = 'error'
                else if (lowerLabel.includes('due today')) chipColor = 'warning'
                else if (lowerLabel.includes('completed')) chipColor = 'success'

                const outcomeDisplay = String(row.original.outcomeName ?? row.original.OutcomeName ?? '').trim()

                return (
                    <div className='flex items-center gap-3'>
                        <div className='flex items-center justify-center flex-shrink-0'>
                            <AppointmentTypeIcon iconClass={getAppointmentTypeIcon(row.original)} size={32} />
                        </div>
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-center gap-2 flex-wrap'>
                                <Typography
                                    sx={{ color: '#666cff', fontSize: '16px', fontWeight: 600, textDecoration: 'underline' }}
                                    className='line-clamp-1 cursor-pointer'
                                    onClick={() => callbacksRef.current.onViewAppointment && callbacksRef.current.onViewAppointment(row.original)}
                                >
                                    {row.original.taskTitle || 'No Title'}
                                </Typography>
                                <Chip
                                    label={statusLabel}
                                    color={chipColor}
                                    variant='filled'
                                    size='small'
                                    className='font-medium text-[10px] h-[20px] px-2'
                                    sx={{
                                        color: '#fff !important',
                                        backgroundColor: (theme) => {
                                            if (chipColor === 'error') return '#ea5455';
                                            if (chipColor === 'success') return '#28c76f';
                                            if (chipColor === 'warning') return '#ff9f43';
                                            if (chipColor === 'info') return '#3b82f6';
                                            return undefined;
                                        }
                                    }}
                                />
                            </div>
                            {outcomeDisplay ? (
                                <Typography
                                    className='font-medium'
                                    sx={{ fontSize: '13px', color: '#28c76f', lineHeight: 1.35 }}
                                >
                                    {`Outcome: ${outcomeDisplay}`}
                                </Typography>
                            ) : null}
                            {row.original.completedDate && (lowerLabel.includes('completed')) && (
                                <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                                    Completed: {formatDate(row.original.completedDate, false)}
                                </Typography>
                            )}
                        </div>
                    </div>
                )
            }
        }),
        columnHelper.accessor('GeoEntityType', {
            header: 'TYPE',
            cell: ({ row }) => {
                const geoType = (
                    row.original.GeoEntityType ||
                    row.original.goScoEntityType ||
                    row.original.goEntityType ||
                    row.original.type ||
                    ''
                ).toLowerCase();
                const isEnquiry = geoType.includes('enquiry') || geoType.includes('enquir');
                return (
                    <Chip
                        label={isEnquiry ? 'Enquiry' : 'Lead'}
                        size='small'
                        sx={{
                            height: '22px',
                            fontSize: '11px',
                            fontWeight: 600,
                            backgroundColor: isEnquiry ? '#ea580c' : '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            '& .MuiChip-label': { px: '8px' }
                        }}
                    />
                );
            },
            enableSorting: false
        }),
        columnHelper.accessor('endDate', {
            header: 'DUE DATE',
            cell: ({ row }) => (
                <Typography className='font-medium' sx={{ fontSize: '14px', fontWeight: 600, color: '#334155 !important' }}>
                    {formatDate(row.original.endDate, false)}
                </Typography>
            )
        }),
        columnHelper.accessor('name', {
            header: 'RELATED TO',
            cell: ({ row }) => (
                <div className='flex items-center gap-3'>
                    {row.original.enquiryId && String(row.original.enquiryId) !== '0' ? (
                        <ScoreCard leadId={row.original.enquiryId}>
                            <div className='flex-shrink-0'>
                                {getAvatar({ avatar: row.original.avatar, name: row.original.name, threshold: row.original.threshold })}
                            </div>
                        </ScoreCard>
                    ) : (
                        <div className='flex-shrink-0'>
                            {getAvatar({ avatar: row.original.avatar, name: row.original.name, threshold: row.original.threshold })}
                        </div>
                    )}
                    <div className='flex flex-col gap-0.5'>
                        <div className='flex items-center gap-2'>
                            <Typography
                                className='cursor-pointer'
                                sx={{ color: '#666cff !important', fontSize: '15px', fontWeight: 600, margin: 0, textDecoration: 'underline' }}
                                onClick={() => {
                                    const leadId = row.original.enquiryId || row.original.EntityId || row.original.LeadId || row.original.EnquiryId || row.original.leadId;
                                    if (callbacksRef.current.onViewLead && leadId) callbacksRef.current.onViewLead(leadId);
                                }}
                            >
                                {row.original.name}
                            </Typography>
                            <LaunchIcon
                                sx={{
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    color: '#94a3b8',
                                    '&:hover': { color: '#666cff' }
                                }}
                                onClick={(e) => { e.stopPropagation(); setSelectedEntity(row.original); setShowEntityEdit(true); }}
                                titleAccess="Edit Entity"
                            />
                        </div>
                        <Typography sx={{ color: '#1e293b', fontSize: '13px', fontWeight: 500, opacity: 0.8 }}>
                            {row.original.phone}
                        </Typography>
                        {row.original.address && (
                            <Typography sx={{ color: '#64748b', fontSize: '12px', fontWeight: 400, opacity: 0.8 }}>
                                {row.original.address}
                            </Typography>
                        )}
                    </div>
                </div>
            )
        }),
        columnHelper.accessor('creatorName', {
            header: 'OWNER',
            cell: ({ row }) => {
                const owner = row.original.creatorName || row.original.OwnerName || 'Mukesh Kumar'
                return (
                    <div className='flex items-center gap-2 min-w-0'>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: '#f4f7fb',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }}
                        >
                            <Icon icon='ri:user-line' width={18} height={18} style={{ color: '#6b7280' }} />
                        </Box>
                        <Typography sx={{ fontSize: '14.5px', fontWeight: 600, color: '#333155 !important' }} className='truncate'>{owner}</Typography>
                    </div>
                )
            }
        }),
        columnHelper.accessor('action', {
            header: 'ACTION',
            cell: ({ row }) => {
                const isCompleted = (row.original.taskStatus || row.original.status || '').toLowerCase().trim() === 'completed' || 
                                   (row.original.statusLabel || '').toLowerCase().trim().includes('completed') || 
                                   (row.original.outcomeName || row.original.OutcomeName || '').trim().length > 0;

                return (
                    <div className='flex items-center justify-center w-full min-w-[80px] gap-2'>
                        <OptionMenu
                            icon={<Icon icon='ri:more-2-fill' fontSize={20} />}
                            iconButtonProps={{
                                sx: {
                                    width: 32,
                                    height: 32,
                                    border: '1px solid #dae1e7',
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    color: '#64748b',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: '#f8fafc',
                                        borderColor: '#666cff',
                                        color: '#666cff',
                                        boxShadow: '0 2px 8px rgba(102, 108, 255, 0.15)'
                                    }
                                }
                            }}
                             options={[
                                crmOperationPerms?.View && { text: 'View Details', icon: <Icon icon="ri:eye-line" style={{ color: '#666cff' }} />, menuItemProps: { onClick: () => callbacksRef.current.onViewAppointment && callbacksRef.current.onViewAppointment(row.original) } },
                                crmOperationPerms?.Edit && { text: 'Edit', icon: <Icon icon="ri:edit-box-line" style={{ color: '#ff9f43' }} />, menuItemProps: { onClick: () => callbacksRef.current.onEditAppointment && callbacksRef.current.onEditAppointment(row.original) } },
                                crmOperationPerms?.Edit && { text: 'Add Comment', icon: <Icon icon="ri:chat-1-line" style={{ color: '#00CFE8' }} />, menuItemProps: { onClick: () => callbacksRef.current.onAddComment && callbacksRef.current.onAddComment(row.original) } },
                                crmOperationPerms?.Edit && !isCompleted && { text: 'Mark Completed', icon: <Icon icon="ri:checkbox-circle-line" style={{ color: '#28c76f' }} />, menuItemProps: { onClick: () => callbacksRef.current.onMarkCompleted && callbacksRef.current.onMarkCompleted(row.original) } },
                                { divider: true },
                                crmOperationPerms?.Delete && { text: 'Remove', icon: <Icon icon="ri:delete-bin-7-line" style={{ color: '#ea5455' }} />, menuItemProps: { onClick: () => callbacksRef.current.onRemoveAppointment && callbacksRef.current.onRemoveAppointment(row.original.id), sx: { color: 'error.main' } } }
                            ].filter(Boolean)}
                        />
                        <OptionMenu
                            icon={<Icon icon='ri:settings-3-line' fontSize={20} />}
                            iconButtonProps={{
                                sx: {
                                    width: 32,
                                    height: 32,
                                    border: '1px solid #dae1e7',
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    color: '#64748b',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: '#f8fafc',
                                        borderColor: '#666cff',
                                        color: '#666cff',
                                        boxShadow: '0 2px 8px rgba(102, 108, 255, 0.15)'
                                    }
                                }
                            }}
                            options={TASK_ACTIONS.map(action => ({
                                text: action.label,
                                icon: <Icon icon={action.icon} fontSize={20} style={{ color: action.color }} />,
                                menuItemProps: {
                                    className: 'flex items-center gap-2',
                                    onClick: () => callbacksRef.current.onViewAppointment && callbacksRef.current.onViewAppointment(row.original, action.id)
                                }
                            }))}
                        />
                    </div>
                );
            },
            enableSorting: false
        })
    ], [crmOperationPerms]);

    // Data now comes from props, no need for local fetch logic.
    const filteredData = useMemo(() => {
        if (statusFilter === 'All') return data;
        return data.filter(t => t.status === statusFilter);
    }, [data, statusFilter]);

    useEffect(() => {
        if (filteredData) {
            setTotalRecords(filteredData.length);
        }
    }, [filteredData]);

    const currentViewData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(start, start + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minHeight: 0 }}>
            <PremiumModuleWrapper
                moduleName="Appointment"
                showCards={false}
                stats={stats}
                onStatusClick={onRefresh}
                loading={loading}
                data={currentViewData}
                columns={columns}
                totalRecords={filteredData.length}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                globalFilter={searchTerm}
            />
            {showEntityEdit && (
                <EntityEdit
                    entityData={selectedEntity}
                    onSave={() => {
                        onRefresh && onRefresh();
                        setShowEntityEdit(false);
                    }}
                    onCancel={() => setShowEntityEdit(false)}
                />
            )}
        </Box>
    )
}

export default PhysicalAppointmentListWrapper
